import { useMutation, useQuery } from '@apollo/client';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { only, up } from 'styled-breakpoints';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import Button from '@rebound/design-system/components/Button';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import {
  DraftReturn,
  DraftReturnItemAction,
} from '@rebound/shared-models/draft-return/draft-return.model';
import { Order } from '@rebound/shared-models/order/order.model';
import {
  ThemeColorScheme,
  UserJourneyEnum,
} from '@rebound/shared-models/retailer/retailer.model';
import { CycleonReturnOrderStatusEnum } from '@rebound/shared-models/return/return.model';

import { HighlightedWrapper } from '@components/HighlightedWrapper';
import ImagePlaceholder, {
  ItemImageVariant,
  PlaceholderInfo,
  PlaceHolderWrapper,
} from '@components/ImagePlaceholder';
import Picture, { NonReturnableOverlay } from '@components/Picture';
import {
  CreateDraftReturnMutation,
  CreateDraftReturnMutationResult,
  DeleteDraftReturnMutation,
  DeleteDraftReturnMutationResult,
  GetUnfinishedDraftReturnQuery,
  GetUnfinishedDraftReturnQueryResult,
} from '@modules/draft-return/draft-return.queries';
import {
  useRetailerJourney,
  useRetailerSubdomain,
} from '@modules/retailer/retailer.context';
import {
  anyItemWithinReturnWindow,
  isItemWithinReturnWindow,
} from '@modules/return-journey/return-journey.utils';

import { currencyFormatter, formatDaysToReturn } from '../dashboard.utils';
import { ReturnActionButtonPlaceholder } from './ReturnActionButtonPlaceholder';
import ReturnNotAvailableLabel from './ReturnNotAvailableLabel';
import ViewMultipleReturnsButton from './ViewMultipleReturnsButton';

export enum OrderCardType {
  Standard = 'Standard',
  LookupPreview = 'LookupPreview',
}

type OrderCardProps = {
  order: Order;
  scheme?: string;
  type?: OrderCardType;
  returnPolicyDays?: number;
  highlighted?: boolean | null;
};

const OrderCard = ({
  order,
  scheme = ThemeColorScheme.Indigo,
  type = OrderCardType.Standard,
  returnPolicyDays = 30,
  highlighted = null,
}: OrderCardProps) => {
  const router = useRouter();
  const retailerJourney = useRetailerJourney();
  const subdomain = useRetailerSubdomain();
  const { t } = useTranslation();
  const [unfinishedDraftReturn, setUnfinishedDraftReturn] = useState<
    DraftReturn | undefined
  >(undefined);
  const [createDraftReturn, { loading: createDraftReturnLoading }] =
    useMutation<CreateDraftReturnMutationResult>(CreateDraftReturnMutation, {
      onCompleted: (data) => {
        (window as any).heap.track('Create return', data.createDraftReturn);

        router.push(`/return/${data.createDraftReturn._id}/select-items`);
      },
      update(cache, { data }) {
        if (!data) {
          return;
        }

        cache.writeQuery({
          query: GetUnfinishedDraftReturnQuery,
          variables: {
            orderReference: data.createDraftReturn.orderId,
          },
          data: { getUnfinishedDraftReturn: data.createDraftReturn },
        });
      },
      onError: () => {
        const message = t('errors.somethingWentWrong');
        toast.error(message);
      },
    });

  const [deleteDraftReturn] = useMutation<DeleteDraftReturnMutationResult>(
    DeleteDraftReturnMutation,
    {
      update(cache, { data }) {
        if (!data) {
          return;
        }
        const normalizedDraftReturnId = cache.identify({
          id: data.deleteDraftReturn._id,
          __typename: 'DraftReturn',
        });
        cache.evict({ id: normalizedDraftReturnId });
        cache.gc();
      },
    },
  );

  const { loading: unfinishedDraftReturnLoading } =
    useQuery<GetUnfinishedDraftReturnQueryResult>(
      GetUnfinishedDraftReturnQuery,
      {
        variables: {
          orderReference: order.id,
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
          if (data?.getUnfinishedDraftReturn && !returnAvailable) {
            deleteDraftReturn({
              variables: {
                draftReturnId: {
                  ...data.getUnfinishedDraftReturn,
                }._id,
                retailerSubdomain: subdomain,
              },
            });
            return;
          }
          setUnfinishedDraftReturn(data?.getUnfinishedDraftReturn);
        },
      },
    );

  const orderItemsWithKeys = order.items.map((item) => ({
    ...item,
    key: uuid(),
    outOfReturnWindow:
      !item.nonReturnable &&
      !item.returned &&
      !isItemWithinReturnWindow(item, order, returnPolicyDays),
  }));

  const orderReturnsItems = useMemo(
    () =>
      order.returns
        ? order.returns
            .map((orderReturn) => orderReturn.items)
            .reduce((acc, x) => [...acc, ...x], [])
        : [],
    [order],
  );

  const orderReturnsItemsWithStatus = useMemo(
    () =>
      order.returns
        ? order.returns
            .map((orderReturn) =>
              orderReturn.items.map((item) => ({
                ...item,
                returnStatus: orderReturn.status,
              })),
            )
            .reduce(
              (combinedItems, itemsFromReturn) => [
                ...combinedItems,
                ...itemsFromReturn,
              ],
              [],
            )
        : [],
    [order],
  );

  const returnSummaryByType = useMemo(() => {
    let refundTotal = 0;
    let storeCreditTotal = 0;
    let exchangeCount = 0;

    const allItems =
      order.returns?.flatMap((orderReturn) => {
        refundTotal += orderReturn.refundValue;
        return orderReturn.items.map((item) => ({
          ...item,
          returnType: item.returnType ?? DraftReturnItemAction.Refund,
        }));
      }) ?? [];

    for (const item of allItems) {
      if (item.returnType === DraftReturnItemAction.ExchangeToCredits) {
        storeCreditTotal += item.storeCreditRefund?.amount ?? 0;
      } else if (item.returnType === DraftReturnItemAction.ExchangeToItem) {
        exchangeCount += 1;
      }
    }

    return {
      refundTotal,
      storeCreditTotal,
      exchangeCount,
    };
  }, [order]);

  const isAllItemsNonReturnable = order.items.every(
    (item) =>
      item.nonReturnable ||
      item.returned ||
      !isItemWithinReturnWindow(item, order, returnPolicyDays),
  );

  const isAllItemsReturned = useMemo(
    () =>
      isAllItemsNonReturnable ||
      order.items.reduce((acc, x) => acc + x.quantity, 0) ===
        orderReturnsItems.reduce((acc, x) => acc + x.quantity, 0),
    [isAllItemsNonReturnable, order.items, orderReturnsItems],
  );

  const returnAvailable =
    !!order.items.find((i) => !i.nonReturnable) &&
    (anyItemWithinReturnWindow(order, returnPolicyDays) ||
      Boolean(order.rma)) &&
    !isAllItemsReturned;

  const orderHasOneReturn =
    order.returns?.length === 1 &&
    type === OrderCardType.Standard &&
    order?.returns[0]?.status === CycleonReturnOrderStatusEnum.DONE &&
    retailerJourney === UserJourneyEnum.EMAIL_VERIFICATION;

  const orderHasMultipleReturns =
    order.returns?.length > 1 && type === OrderCardType.Standard;

  const returnNotAvailableAndNoExistingReturns =
    (!returnAvailable &&
      !order.returns?.length &&
      type === OrderCardType.Standard) ||
    (retailerJourney === UserJourneyEnum.ORDER_LOOKUP && !returnAvailable);

  const salesOrderHasAllItemsReturned = order.items?.every(
    (item) =>
      item?.returned === true ||
      item?.nonReturnable === true ||
      !isItemWithinReturnWindow(item, order, returnPolicyDays),
  );

  const continueReturnUrl =
    unfinishedDraftReturn &&
    unfinishedDraftReturn._id &&
    unfinishedDraftReturn.currentStepSlug
      ? `/return/${
          unfinishedDraftReturn._id
        }/${unfinishedDraftReturn.currentStepSlug.replace(/['"]/g, '')}`
      : '/';

  const deliveryWasPaid = unfinishedDraftReturn?.deliveryWasPaid === false;

  const hasRefundTotal =
    returnSummaryByType.refundTotal != null &&
    returnSummaryByType.refundTotal > 0;

  const hasStoreCreditTotal = returnSummaryByType.storeCreditTotal > 0;

  const hasExchangeCount = returnSummaryByType.exchangeCount > 0;

  const hasReturnSummary =
    hasRefundTotal || hasStoreCreditTotal || hasExchangeCount;

  return (
    <Wrapper
      id={order.orderReference}
      data-cy="order-card-item"
      scheme={scheme}
      highlighted={highlighted}
    >
      <OrderReference
        hasBottomOffset={returnNotAvailableAndNoExistingReturns}
        data-cy="order-reference-title-information"
      >
        {`${t('ordersListing.orderReferenceLabel')} #${order.orderReference}`}
      </OrderReference>
      <OrderInfoWrapper>
        {/*<OrderInfo>*/}
        {/*  <OrderInfoTitle>{t('ordersListing.orderDateLabel')}</OrderInfoTitle>*/}
        {/*  <p>{formatDate(order.orderDate)}</p>*/}
        {/*</OrderInfo>*/}

        <OrderInfo>
          <OrderInfoTitle>
            {t('ordersListing.orderDeliveryAddressLabel') as string}
          </OrderInfoTitle>
          <p>
            {order.addr1 ? `${order.addr1}, ` : ''}
            {order.city ? `${order.city}, ` : ''}
            {order.country ? `${order.country}, ` : ''}
            {order.state ? `${order.state}, ` : ''}
            {order.zip}
          </p>
        </OrderInfo>
        {!isAllItemsNonReturnable && (
          <OrderInfo>
            <OrderInfoTitle>
              {t('ordersListing.daysToReturnLabel') as string}
            </OrderInfoTitle>
            <p>{formatDaysToReturn(order.orderDate, returnPolicyDays)}</p>
          </OrderInfo>
        )}
        {order.returns?.length && hasReturnSummary ? (
          <OrderInfo>
            <OrderInfoTitle>
              {t('ordersListing.returnSummaryLabel') as string}
            </OrderInfoTitle>

            {hasRefundTotal && (
              <p>
                {currencyFormatter(order.currencyCode).format(
                  returnSummaryByType.refundTotal,
                )}
                &nbsp;{t('returnsListing.returnSummaryRefund') as string}
              </p>
            )}

            {hasStoreCreditTotal && (
              <p>
                {currencyFormatter(order.currencyCode).format(
                  returnSummaryByType.storeCreditTotal,
                )}
                &nbsp;
                {t('returnsListing.returnSummaryStoreCreditRefund') as string}
              </p>
            )}

            {hasExchangeCount && (
              <p>
                {t('returnsListing.returnSummaryExchangeItems', {
                  count: returnSummaryByType.exchangeCount,
                })}
              </p>
            )}
          </OrderInfo>
        ) : null}
      </OrderInfoWrapper>
      <OrderItemsTitle>
        {t('ordersListing.orderItemsSubtitle') as string}
      </OrderItemsTitle>
      <OrderItemsWrapper>
        {orderItemsWithKeys.map((item) => (
          <OrderItem key={item.key}>
            <OrderItemImageWrapper>
              {item.imgPath ? (
                <Picture src={item.imgPath} alt={item.skuDesc} loading="lazy" />
              ) : (
                <PlaceHolderWrapper>
                  <ImagePlaceholder variant={ItemImageVariant.Hanger} />
                  <PlaceholderInfo>
                    {t('ordersListing.noItemImageLabel') as string}
                  </PlaceholderInfo>
                </PlaceHolderWrapper>
              )}
              {item.nonReturnable ? (
                <NonReturnableOverlay>
                  {t('ordersListing.nonReturnableItemLabel') as string}
                </NonReturnableOverlay>
              ) : item.outOfReturnWindow ? (
                <ItemOutOfReturnWindowOverlay data-cy="item-out-of-return-window-overlay">
                  <XCircleIcon />
                  <p>{t('ordersListing.itemOutOfReturnWindowLabel')}</p>
                </ItemOutOfReturnWindowOverlay>
              ) : null}
              {orderReturnsItemsWithStatus.reduce(
                (acc, x) =>
                  x.cycleonId === item.cycleonId &&
                  CycleonReturnOrderStatusEnum.DONE === x.returnStatus
                    ? acc + x.quantity
                    : acc,
                0,
              ) === item.quantity || item.returned ? (
                <ItemWasReturnedOverlay>
                  <CheckCircleIcon />
                  <p>{t('ordersListing.itemWasReturnedLabel')}</p>
                </ItemWasReturnedOverlay>
              ) : null}
            </OrderItemImageWrapper>
            <ItemDescription>{item.skuDesc}</ItemDescription>
            {item.price != null && item.price >= 0 && (
              <ItemInfo>
                {currencyFormatter(order.currencyCode).format(
                  Number(item.price),
                )}
              </ItemInfo>
            )}
          </OrderItem>
        ))}
      </OrderItemsWrapper>
      <OrderLabel>
        {orderHasOneReturn ? (
          <ControlsWrapper>
            <Link
              href={`/dashboard/returns?highlightReturn=${order.returns[0].id}`}
              passHref
            >
              <ViewReturnButton
                scheme={scheme}
                title={t('ordersListing.viewReturnLabel')}
              />
            </Link>
          </ControlsWrapper>
        ) : orderHasMultipleReturns ? (
          <ViewMultipleReturnsButton
            returnIds={order.returns
              .filter((r) => r.status === CycleonReturnOrderStatusEnum.DONE)
              .map((r) => r.id)}
            orderReference={order.orderReference}
          />
        ) : null}
        {!salesOrderHasAllItemsReturned ? (
          deliveryWasPaid || returnAvailable ? (
            !unfinishedDraftReturnLoading &&
            unfinishedDraftReturn &&
            !unfinishedDraftReturn.returnWasCreated ? (
              <Link href={continueReturnUrl} passHref>
                <ReturnActionButton
                  loading={createDraftReturnLoading}
                  disabled={createDraftReturnLoading}
                  scheme={scheme}
                  title={t('ordersListing.continueReturnLabel')}
                />
              </Link>
            ) : !unfinishedDraftReturnLoading &&
              (!unfinishedDraftReturn ||
                unfinishedDraftReturn.returnWasCreated) ? (
              <ReturnActionButton
                scheme={scheme}
                title={t('ordersListing.createReturnLabel')}
                onClick={() =>
                  createDraftReturn({
                    variables: {
                      orderId: order.id,
                      identifier: order.email ? order.email : order.zip,
                    },
                  })
                }
                loading={createDraftReturnLoading}
                disabled={createDraftReturnLoading}
                data-cy="create-return-button"
              />
            ) : (
              <ReturnActionButtonPlaceholder />
            )
          ) : returnNotAvailableAndNoExistingReturns ? (
            <ReturnNotAvailableLabel
              order={order}
              returnPolicyDays={returnPolicyDays}
            />
          ) : null
        ) : null}
      </OrderLabel>
    </Wrapper>
  );
};

const Wrapper = styled(HighlightedWrapper)<{
  scheme: string;
  highlighted: boolean | null;
}>`
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: ${(props) => `1px solid ${props.theme.colors.grayLight3}`};
  padding: 20px 0;
  margin-bottom: 20px;
  position: relative;
  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.04),
    0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  transition:
    transform 2s cubic-bezier(0.165, 0.84, 0.44, 1),
    box-shadow 2s cubic-bezier(0.165, 0.84, 0.44, 1),
    border-color 3s;

  ${up('sm')} {
    margin-bottom: 36px;
    padding: 24px 16px 26px;
  }
`;

const OrderReference = styled.p<{ hasBottomOffset: boolean }>`
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;

  margin-bottom: ${(props) => (props.hasBottomOffset ? '40px' : '22px')};

  ${only('xs')} {
    padding: 0 16px 21px;
    margin-bottom: 0;
    border-bottom: 1px solid ${(props) => props.theme.colors.grayLight4};
  }
`;

const OrderInfoWrapper = styled.div`
  ${only('xs')} {
    & > *:nth-child(odd) {
      background-color: ${({ theme }) => theme.colors.grayLight};
    }
  }

  ${up('sm')} {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  ${only('sm')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const OrderInfo = styled.div`
  width: 100%;
  box-sizing: border-box;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;

  ${only('xs')} {
    padding: 12px;
  }

  ${up('sm')} {
    width: auto;
    min-width: 170px;
  }
`;

const OrderInfoTitle = styled.p`
  color: ${(props) => props.theme.colors.grayDark};
  margin-bottom: 4px;
`;

const OrderItemsTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  margin: 24px 0 10px;
  color: ${(props) => props.theme.colors.darkenBlack2};

  ${only('xs')} {
    padding: 0 12px;
  }
`;

const OrderItemsWrapper = styled.div`
  display: flex;
  overflow: hidden;
  overflow-x: auto;
  padding-left: 12px;

  ${only('xs')} {
    margin-bottom: 24px;
  }
`;

const OrderItem = styled.div`
  margin-right: 16px;
  width: 160px;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;

  ${up('sm')} {
    width: 144px;
  }
`;

const OrderItemImageWrapper = styled.div`
  width: 160px;
  height: 202px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 4px;

  ${up('sm')} {
    width: 144px;
    height: 160px;
  }

  img {
    height: 100%;
    max-height: 100%;
    z-index: -10;
  }
`;

const ItemWasReturnedOverlay = styled(NonReturnableOverlay)`
  background: rgba(107, 114, 128, 0.8);
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  flex-direction: column;

  svg {
    width: 60px;
    height: 60px;
    margin-bottom: 10px;

    path {
      fill: ${(props) => props.theme.colors.white};
    }
  }
`;

const ItemOutOfReturnWindowOverlay = styled(NonReturnableOverlay)`
  background: rgba(107, 114, 128, 0.8);
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  flex-direction: column;

  svg {
    width: 60px;
    height: 60px;
    margin-bottom: 10px;

    path {
      fill: ${(props) => props.theme.colors.white};
    }
  }
`;

const ItemDescription = styled.p`
  max-width: 100%;
  color: ${(props) => props.theme.colors.grayDark5};
  margin-bottom: 8px;
`;

const ItemInfo = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
`;

const OrderLabel = styled.div`
  ${only('xs')} {
    width: auto;
    margin: 0 12px;
  }

  ${up('sm')} {
    position: absolute;
    top: 24px;
    right: 16px;
    display: flex;
  }
`;

const ReturnActionButton = styled(Button)<{ scheme: string }>`
  width: 100%;

  ${only('xs')} {
    display: flex;
    justify-content: center;
    margin-top: 10px;
  }

  ${up('sm')} {
    min-width: 170px;
    width: auto;
    text-align: center;
    margin-left: 16px;
  }

  button {
    background-color: ${(props) => colorSchemeConfig[props.scheme].v600};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    font-weight: 500;
    padding: 10px 20px;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${up('sm')} {
    flex-direction: row;
  }
`;

const ViewReturnButton = styled(ReturnActionButton)`
  margin: 0;

  button {
    background-color: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.grayDark2};
    color: ${(props) => props.theme.colors.grayDark3};
  }
`;

export default OrderCard;
